import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Loader2,
  Layers,
  FileText,
  ListChecks,
  CheckCircle,
  ShieldCheck,
  Lock,
  Monitor,
  UserCheck,
  Mail,
  MessageCircle,
  Clock,
  Globe
} from "lucide-react";
import { BRAND_NAME, BRAND_SUBTITLE } from "../components/ui/Logo";
import { startSubscriptionCheckout } from "../lib/publicCheckout";
import { isUsMarketingHost } from "../lib/usMarketHost";

const CONTACT_EMAIL =
  process.env.REACT_APP_CONTACT_EMAIL || "contacto@aurorageriatrica.com";

const WHATSAPP_DISPLAY = "+502 3060 4078";
const WHATSAPP_HREF = "https://wa.me/50230604078";

function LatamTrialTrustCard({ className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-emerald-200/90 bg-[#EEF8F1] p-3 shadow-[0_2px_16px_rgba(16,185,129,0.07)] sm:p-3.5 lg:p-2.5 lg:pr-3.5 ${className}`}
    >
      <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:gap-4 lg:gap-y-0">
        <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center lg:items-center lg:gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-emerald-200 bg-white text-emerald-700 shadow-sm lg:h-9 lg:w-9">
            <ShieldCheck className="h-6 w-6 lg:h-5 lg:w-5" strokeWidth={1.75} aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-bold uppercase tracking-tight text-slate-900 sm:text-[1.35rem] sm:leading-tight lg:text-lg lg:leading-tight">
              14 DÍAS GRATIS
            </h2>
            <p className="mt-0.5 text-xs font-bold uppercase tracking-wide text-emerald-900/90 sm:text-sm lg:text-[11px] lg:leading-snug">
              SIN TARJETA DE CRÉDITO
            </p>
          </div>
        </div>
        <ul className="flex flex-col gap-1 text-xs font-medium text-slate-800 sm:text-sm lg:mt-0 lg:shrink-0 lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-3 lg:gap-y-0.5 lg:text-[11px]">
          <li className="flex items-center gap-1.5">
            <CheckCircle className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
            Acceso inmediato
          </li>
          <li className="flex items-center gap-1.5">
            <CheckCircle className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
            Cancelación libre
          </li>
          <li className="flex items-center gap-1.5">
            <CheckCircle className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
            Sin cobros automáticos
          </li>
        </ul>
      </div>
      <p className="mt-2 flex items-start gap-1.5 text-[10px] leading-snug text-slate-600 lg:mt-1.5">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700" aria-hidden />
        Pago seguro con Lemon Squeezy (EE. UU.)
      </p>
    </div>
  );
}

function LatamBenefitCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-2.5 shadow-sm sm:p-3">
      <div className="flex gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-sky-100 bg-sky-50 text-sky-700 sm:h-9 sm:w-9">
          <Icon className="h-4 w-4 sm:h-[1.05rem] sm:w-[1.05rem]" strokeWidth={2} aria-hidden />
        </span>
        <div className="min-w-0">
          <h3 className="text-xs font-semibold leading-snug text-slate-900 sm:text-sm">{title}</h3>
          <p className="mt-0.5 text-[11px] leading-relaxed text-slate-600 sm:text-xs">{body}</p>
        </div>
      </div>
    </div>
  );
}

function LatamContactCard() {
  return (
    <div className="rounded-2xl border border-sky-200/90 bg-sky-50/50 p-3 shadow-sm">
      <h3 className="text-xs font-semibold text-slate-900 sm:text-sm">¿Necesitas ayuda?</h3>
      <p className="mt-0.5 text-[11px] leading-relaxed text-slate-600 sm:text-xs">Estamos aquí para apoyarte.</p>
      <ul className="mt-2.5 space-y-2 text-[11px] text-slate-700 sm:text-xs">
        <li className="flex items-start gap-2">
          <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600" aria-hidden />
          <a href={`mailto:${CONTACT_EMAIL}`} className="break-all font-medium text-sky-800 underline-offset-2 hover:underline">
            {CONTACT_EMAIL}
          </a>
        </li>
        <li className="flex items-start gap-2">
          <MessageCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600" aria-hidden />
          <a
            href={WHATSAPP_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-sky-800 underline-offset-2 hover:underline"
          >
            WhatsApp: {WHATSAPP_DISPLAY}
          </a>
        </li>
        <li className="flex items-start gap-2">
          <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600" aria-hidden />
          <span>Lunes a viernes 8:00 - 18:00 (GMT-6)</span>
        </li>
      </ul>
    </div>
  );
}

const BENEFITS_US = [
  { icon: Layers, title: "20+ integrated geriatric scales" },
  { icon: FileText, title: "Concise, professional visit documentation" },
  { icon: ListChecks, title: "Guided workflow from intake to plan" }
];

function loginCopy(isUs) {
  if (isUs) {
    return {
      brandName: "Aurora Geriatric",
      brandSubtitle: "Clinical platform for U.S. geriatric care",
      showEditionBadge: true,
      heroTitle: "Structure geriatric visits with less documentation drag",
      heroSub:
        "Built for U.S. outpatient practice—cognition, function, safety, and longitudinal context in one place.",
      quote: "Less time at the keyboard. More time with patients.",
      tabLogin: "Sign in",
      tabRegister: "Create your account",
      email: "Email",
      password: "Password",
      confirmPassword: "Confirm password",
      fullName: "Full name",
      placeholderName: "Dr. Jane Smith",
      placeholderEmail: "you@clinic.com",
      signIn: "Sign in",
      signingIn: "Signing in…",
      registerCta: "Start free trial",
      buyNow: "Buy now",
      postTrial: "Start free trial",
      postDemo: "Clinical demo",
      postSubscribe: "Subscribe now — $69/mo",
      registerHint:
        "Create your account to start your trial, or buy now to go straight to checkout.",
      support: "Support",
      supportLead: "Can't sign in?",
      passwordSettings: "Password changes live in Settings after you sign in.",
      checkoutToast: "Payment complete. Sign in with your Aurora account email and password.",
      welcomeToast: "Welcome back.",
      accountCreatedToast: "You're all set.",
      loginError: "Could not sign in",
      registerError: "Could not create account",
      pwdMismatch: "Passwords do not match",
      supportMailSubject: "Aurora%20Geriatric%20%E2%80%94%20access"
    };
  }
  return {
    brandName: BRAND_NAME,
    brandSubtitle: BRAND_SUBTITLE,
    showEditionBadge: false,
    heroTitle: "Organice su consulta geriátrica en un solo lugar",
    heroSub: "Herramienta clínica para documentar, valorar y ahorrar tiempo en cada visita al adulto mayor.",
    quote: "Más claridad clínica. Menos carga administrativa.",
    tabLogin: "Iniciar sesión",
    tabRegister: "Registrarse",
    email: "Correo electrónico",
    password: "Contraseña",
    confirmPassword: "Confirmar contraseña",
    fullName: "Nombre completo",
    placeholderName: "Dr. Juan Pérez",
    placeholderEmail: "doctor@clinica.com",
    signIn: "Iniciar sesión",
    signingIn: "Ingresando...",
    registerCta: "REGISTRARME",
    buyNow: "Comprar ahora",
    postTrial: "Probar gratis 14 días",
    postDemo: "Ver demostración",
    postSubscribe: "Suscribirse ahora — $39/mes",
    registerHint:
      "Elija crear su cuenta para iniciar la prueba o comprar ahora para ir directo al checkout.",
    support: "Soporte",
    supportLead: "¿No puede acceder?",
    passwordSettings: "Cambio de contraseña en Configuración.",
    checkoutToast: "Pago completado. Inicie sesión con el correo y contraseña de su cuenta Aurora.",
    welcomeToast: "¡Bienvenido!",
    accountCreatedToast: "Cuenta creada exitosamente",
    loginError: "Error al iniciar sesión",
    registerError: "Error al registrarse",
    pwdMismatch: "Las contraseñas no coinciden",
    supportMailSubject: "Aurora%20Geri%C3%A1trica%20%E2%80%94%20acceso"
  };
}

const handleSubscriptionCheckout = () => {
  startSubscriptionCheckout();
};

function PostAuthActions({ isUs }) {
  const s = loginCopy(isUs);
  return (
    <div className="mt-5 space-y-3 border-t border-slate-100 pt-5">
      <div className="grid grid-cols-1 gap-2.5">
        <Button
          type="button"
          variant="outline"
          asChild
          className="h-10 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
          data-testid="login-demo-button"
        >
          <Link to="/demo-clinico">Ver demo en vivo — sin registro</Link>
        </Button>
      </div>
      {isUs && (
        <Button
          type="button"
          variant="link"
          className="h-auto w-full rounded-lg py-2 text-xs font-normal text-slate-500 no-underline hover:bg-slate-50/80 hover:text-slate-700 hover:underline"
          data-testid="login-subscribe-button"
          onClick={handleSubscriptionCheckout}
        >
          {s.postSubscribe}
        </Button>
      )}
    </div>
  );
}

function RegisterPurchaseActions({ loading, isUs, acceptedTerms, setAcceptedTerms }) {
  const s = loginCopy(isUs);
  const primaryClass = isUs
    ? "h-11 rounded-xl bg-slate-900 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
    : "h-11 w-full rounded-xl bg-sky-600 text-sm font-bold uppercase tracking-wide text-white shadow-sm transition-all hover:bg-sky-700";
  const submitDisabled = loading || (!isUs && !acceptedTerms);
  return (
    <div className={isUs ? "mt-5 space-y-3 border-t border-slate-100 pt-5" : "mt-3 space-y-2 border-t border-slate-100 pt-3"}>
      {isUs ? (
        <>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            <Button
              type="submit"
              form="register-form"
              className={primaryClass}
              disabled={loading}
              data-testid="register-trial-button"
            >
              {s.registerCta}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-11 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50"
              onClick={handleSubscriptionCheckout}
              data-testid="register-buy-now-button"
            >
              {s.buyNow}
            </Button>
          </div>
          <p className="text-center text-[11px] leading-relaxed text-slate-500">{s.registerHint}</p>
        </>
      ) : (
        <>
          <Button
            type="submit"
            form="register-form"
            className={primaryClass}
            disabled={submitDisabled}
            data-testid="register-trial-button"
          >
            {s.registerCta}
          </Button>
          <div className="flex items-start gap-2 mt-2">
            <input
              type="checkbox"
              id="accept-terms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-sky-600 cursor-pointer"
            />
            <label htmlFor="accept-terms" className="text-[11px] leading-relaxed text-slate-500 cursor-pointer">
              He leído y acepto los{" "}
              <Link to="/terminos" className="font-semibold text-sky-700 hover:underline" target="_blank" rel="noopener noreferrer">
                Términos y Condiciones
              </Link>{" "}
              y la{" "}
              <Link to="/privacidad" className="font-semibold text-sky-700 hover:underline" target="_blank" rel="noopener noreferrer">
                Política de Privacidad
              </Link>{" "}
              de Aurora Geriátrica.
            </label>
          </div>
        </>
      )}
    </div>
  );
}

export default function Login({ defaultTab = "login", modoAula: modoAulaProp = false }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [nombre, setNombre] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register, isAula } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isUs = isUsMarketingHost();
  const s = useMemo(() => loginCopy(isUs), [isUs]);
  const modoAula =
    modoAulaProp ||
    location.pathname === "/register/aula" ||
    new URLSearchParams(location.search).get("modo") === "aula";

  const postAuthPath = useCallback(() => {
    const q = new URLSearchParams(location.search);
    const next = q.get("next");
    if (next && next.startsWith("/") && !next.startsWith("//")) {
      return next;
    }
    if (isAula) return "/aula/dashboard";
    return "/dashboard";
  }, [location.search, isAula]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.search.includes("checkout=success")) {
      toast.success(loginCopy(isUsMarketingHost()).checkoutToast);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      toast.success(s.welcomeToast);
      const mode = result?.account_mode || result?.user?.account_mode;
      const dest = mode === "academic" ? "/aula/dashboard" : postAuthPath();
      navigate(dest, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || s.loginError);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error(s.pwdMismatch);
      return;
    }
    setLoading(true);
    try {
      const registration_token =
        new URLSearchParams(window.location.search).get("token") || "";
      const result = await register(email, password, nombre, {
        account_mode: modoAula ? "academic" : "clinical",
        registration_token,
      });
      toast.success(s.accountCreatedToast);
      const mode = result?.account_mode || result?.user?.account_mode;
      const dest = mode === "academic" ? "/aula/dashboard" : postAuthPath();
      navigate(dest, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || s.registerError);
    } finally {
      setLoading(false);
    }
  };

  const passwordsDoNotMatch = confirmPassword.length > 0 && password !== confirmPassword;

  const benefits = BENEFITS_US;

  const asideOrder = isUs ? "order-2 lg:order-1" : "order-3 lg:order-1";
  const mainOrder = isUs ? "order-1 lg:order-2" : "order-2 lg:order-2";

  const asideClass = isUs
    ? `${asideOrder} flex min-h-0 flex-1 flex-col justify-center border-slate-200/70 bg-gradient-to-b from-slate-50 via-white to-indigo-50/30 px-5 py-8 lg:border-r lg:px-10 xl:px-14 lg:py-12`
    : `${asideOrder} flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden px-4 pb-6 pt-1 sm:px-5 lg:h-dvh lg:max-h-dvh lg:min-h-0 lg:min-w-0 lg:overflow-hidden lg:px-0 lg:pb-0 lg:pt-0`;

  const shellClass = isUs
    ? "flex min-h-dvh flex-col bg-[#f4f6f8] lg:h-dvh lg:max-h-dvh lg:flex-row lg:overflow-hidden"
    : "flex min-h-dvh flex-col overflow-x-hidden overflow-y-auto bg-gradient-to-br from-slate-50 via-white to-sky-50/35 lg:mx-auto lg:grid lg:h-dvh lg:max-h-dvh lg:min-h-0 lg:w-full lg:max-w-[min(1440px,85vw)] lg:grid-cols-[minmax(0,55fr)_minmax(0,45fr)] lg:items-stretch lg:gap-x-4 lg:overflow-hidden lg:px-3 lg:py-3 xl:gap-x-5 xl:px-4";

  const cardRing = isUs ? "ring-indigo-950/[0.04]" : "ring-slate-900/[0.03]";

  const cardClassName = isUs
    ? `overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_20px_50px_rgba(15,23,42,0.08)] ring-1 ${cardRing}`
    : "overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_14px_40px_-12px_rgba(15,23,42,0.1)]";

  const inputFieldClassUs =
    "h-11 w-full rounded-xl border-slate-200 bg-white shadow-sm transition-shadow focus-visible:border-sky-400 focus-visible:ring-2 focus-visible:ring-sky-500/35 focus-visible:ring-offset-0";
  const inputFieldClassLatam =
    "h-10 w-full rounded-xl border-slate-200 bg-white shadow-sm transition-shadow focus-visible:border-sky-400 focus-visible:ring-2 focus-visible:ring-sky-500/35 focus-visible:ring-offset-0";
  const inputFieldClass = isUs ? inputFieldClassUs : inputFieldClassLatam;

  return (
    <div className={shellClass}>
      {!isUs && !modoAula && (
        <div className="order-1 shrink-0 px-4 pt-4 sm:px-5 lg:hidden">
          <div className="mx-auto w-full max-w-2xl">
            <LatamTrialTrustCard />
          </div>
        </div>
      )}

      <aside className={asideClass}>
        {modoAula && !isUs ? (
          <div className="relative flex h-full min-h-[320px] flex-col overflow-hidden rounded-2xl bg-slate-900">
            <img
              src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80"
              alt="Formación médica universitaria"
              className="absolute inset-0 h-full w-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
            <div className="relative flex flex-1 flex-col justify-end p-8">
              <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-violet-400">
                Plataforma académica
              </p>
              <h2 className="mb-4 text-2xl font-bold leading-snug text-white">
                Formación en Valoración
                <br />
                Geriátrica Integral
              </h2>
              <ul className="space-y-3">
                {[
                  "Practica con casos clínicos reales",
                  "Realiza Valoraciones Geriátricas Integrales completas",
                  "Aprende a interpretar resultados y generar informes clínicos",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-500/30 text-violet-400">
                      ✓
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : isUs ? (
          <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center overflow-visible lg:mx-0 lg:max-w-lg">
            <div className="mb-7 flex w-full flex-col items-start overflow-visible pt-1 text-left sm:mb-9 lg:mb-10">
              <div className="mb-5 w-full max-w-full sm:mb-6">
                <img
                  src="/assets/logo/logo-wordmark.png"
                  alt={s.brandName}
                  className="block h-auto w-full max-w-[min(100%,22rem)] bg-transparent object-contain object-left sm:max-w-[24rem]"
                />
              </div>
              {s.showEditionBadge && (
                <p className="mb-2 inline-flex items-center rounded-full border border-sky-200/90 bg-sky-50/90 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-900/80">
                  U.S. Edition
                </p>
              )}
              <p className="text-lg font-semibold tracking-tight text-slate-900">{s.brandName}</p>
              <p className="mt-2.5 max-w-[22rem] text-sm leading-relaxed text-slate-500">{s.brandSubtitle}</p>
            </div>

            <h1 className="text-2xl font-semibold leading-snug tracking-tight text-slate-900 sm:text-[1.75rem] sm:leading-tight xl:text-[2rem]">
              {s.heroTitle}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.heroSub}</p>

            <ul className="mt-8 space-y-3">
              {benefits.map(({ icon: Icon, title }) => (
                <li
                  key={title}
                  className="flex min-h-[3.75rem] items-center gap-3 rounded-xl border border-slate-200 bg-white/90 px-3.5 py-3 shadow-sm transition-shadow hover:shadow-md sm:min-h-[4rem] sm:gap-4 sm:px-4 sm:py-3.5"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-sky-100 bg-sky-50 text-sky-700">
                    <Icon className="h-[1.15rem] w-[1.15rem]" strokeWidth={2} />
                  </span>
                  <span className="text-sm font-medium leading-snug text-slate-800">{title}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 rounded-xl border-l-4 border-sky-500 bg-sky-50/70 py-3 pl-4 pr-3 sm:mt-8">
              <p className="text-sm font-semibold leading-snug text-slate-800">{s.quote}</p>
            </div>

            <p className="mt-8 hidden text-xs text-slate-400 lg:block">
              {s.brandName} · © {new Date().getFullYear()}
            </p>
          </div>
        ) : (
          <>
            <div className="mx-auto hidden h-full min-h-0 w-full max-w-none min-w-0 flex-1 flex-col gap-2 lg:flex lg:max-h-dvh">
              <img
                src="/assets/logo/logo-wordmark.png"
                alt={s.brandName}
                className="block h-auto max-w-[168px] shrink-0 bg-transparent object-contain object-left lg:max-w-[180px]"
              />
              <LatamTrialTrustCard className="shrink-0" />
              <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto overscroll-y-contain pr-0.5 [-webkit-overflow-scrolling:touch]">
                <LatamBenefitCard
                  icon={Monitor}
                  title="Plataforma clínica integral para evaluación geriátrica"
                  body="Todo lo que necesitas para brindar mejor atención a tus pacientes."
                />
                <LatamBenefitCard
                  icon={ShieldCheck}
                  title="Seguridad y privacidad médica de nivel internacional"
                  body="Cumplimos estándares de protección de datos y buenas prácticas clínicas."
                />
                <LatamBenefitCard
                  icon={UserCheck}
                  title="Diseñada para profesionales de la salud"
                  body="Interfaz simple, rápida e intuitiva para tu día a día."
                />
                <LatamContactCard />
              </div>
            </div>
            <div className="mx-auto flex w-full max-w-2xl flex-col gap-3 lg:hidden">
              <img
                src="/assets/logo/logo-wordmark.png"
                alt={s.brandName}
                className="block h-auto max-w-[200px] w-full bg-transparent object-contain object-left"
              />
              <LatamBenefitCard
                icon={Monitor}
                title="Plataforma clínica integral para evaluación geriátrica"
                body="Todo lo que necesitas para brindar mejor atención a tus pacientes."
              />
              <LatamBenefitCard
                icon={ShieldCheck}
                title="Seguridad y privacidad médica de nivel internacional"
                body="Cumplimos estándares de protección de datos y buenas prácticas clínicas."
              />
              <LatamBenefitCard
                icon={UserCheck}
                title="Diseñada para profesionales de la salud"
                body="Interfaz simple, rápida e intuitiva para tu día a día."
              />
              <LatamContactCard />
            </div>
          </>
        )}
      </aside>

      <main
        className={
          isUs
            ? `${mainOrder} flex min-h-0 w-full min-w-0 flex-1 flex-col items-center justify-center overflow-y-auto overflow-x-hidden px-4 py-7 sm:px-5 sm:py-8 lg:w-[min(100%,26rem)] lg:shrink-0 lg:px-6 xl:w-[28rem]`
            : `${mainOrder} flex min-h-0 w-full min-w-0 flex-1 flex-col items-stretch justify-start overflow-x-hidden overflow-y-auto px-4 py-4 sm:px-5 lg:max-h-dvh lg:min-h-0 lg:min-w-0 lg:max-w-none lg:justify-start lg:self-stretch lg:overflow-y-auto lg:px-0 lg:py-0`
        }
      >
        <div className={`w-full animate-slide-up ${isUs ? "max-w-md" : "max-w-2xl lg:max-w-none"}`}>
          <Card className={cardClassName}>
            {!isUs && (
              <div className="border-b border-slate-100 px-4 pb-2.5 pt-3 sm:px-5">
                <h2 className="text-lg font-semibold tracking-tight text-slate-900">
                  {modoAula ? "Registro — Aurora Aula" : "Crea tu cuenta médica"}
                </h2>
                <p className="mt-0.5 text-xs leading-relaxed text-slate-600 sm:text-sm">
                  {modoAula
                    ? "Crea tu cuenta de estudiante"
                    : "Empieza tu prueba gratuita y conoce el flujo clínico de Aurora."}
                </p>
              </div>
            )}
            <Tabs defaultValue={defaultTab} className="w-full">
              <div className="border-b border-slate-100 bg-slate-50/50 px-3 pb-1.5 pt-2 sm:px-4">
                <TabsList className="grid h-9 w-full grid-cols-2 gap-0.5 rounded-lg bg-slate-100/90 p-0.5">
                  <TabsTrigger
                    value="login"
                    className={`rounded-lg text-xs font-semibold sm:text-sm ${
                      !isUs
                        ? "text-slate-500 data-[state=active]:bg-sky-600 data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-slate-500"
                        : "data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
                    }`}
                    data-testid="login-tab"
                  >
                    {s.tabLogin}
                  </TabsTrigger>
                  <TabsTrigger
                    value="register"
                    className={`rounded-lg text-xs font-semibold sm:text-sm ${
                      !isUs
                        ? "text-slate-500 data-[state=active]:bg-sky-600 data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-slate-500"
                        : "data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
                    }`}
                    data-testid="register-tab"
                  >
                    {s.tabRegister}
                  </TabsTrigger>
                </TabsList>
              </div>

              <CardContent className={`space-y-0 ${isUs ? "p-4 sm:p-5" : "p-3 sm:p-4"}`}>
                <TabsContent value="login" className="mt-0 outline-none">
                  {isUs && (
                    <div className="mb-5 space-y-1">
                      <h2 className="text-lg font-semibold tracking-tight text-slate-900">Bienvenido de nuevo</h2>
                      <p className="text-sm leading-relaxed text-slate-600">Ingresa para continuar tu práctica clínica.</p>
                    </div>
                  )}
                  <form onSubmit={handleLogin} className={isUs ? "space-y-4" : "space-y-3"}>
                    <div className="space-y-1.5">
                      <Label htmlFor="login-email" className="text-xs font-medium text-slate-700">
                        {s.email}
                      </Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder={s.placeholderEmail}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="login-email-input"
                        className={inputFieldClass}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="login-password" className="text-xs font-medium text-slate-700">
                        {s.password}
                      </Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="login-password-input"
                        className={inputFieldClass}
                      />
                    </div>
                    <Button
                      type="submit"
                      className={
                        isUs
                          ? "mt-1 h-12 w-full rounded-xl bg-slate-900 font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
                          : "mt-1 h-12 w-full rounded-xl bg-sky-600 font-semibold text-white shadow-sm transition-colors hover:bg-sky-700 hover:shadow-md"
                      }
                      disabled={loading}
                      data-testid="login-submit-button"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {s.signingIn}
                        </>
                      ) : (
                        s.signIn
                      )}
                    </Button>
                    <p className="text-center text-[11px] leading-relaxed text-slate-500">
                      {s.supportLead}{" "}
                      <a
                        className="font-semibold text-sky-700 underline-offset-2 hover:underline"
                        href={`mailto:${CONTACT_EMAIL}?subject=${s.supportMailSubject}`}
                      >
                        {s.support}
                      </a>
                      . {s.passwordSettings}
                    </p>
                  </form>
                  <PostAuthActions isUs={isUs} />
                </TabsContent>

                <TabsContent value="register" className="mt-0 outline-none">
                  {!isUs && !modoAula && (
                    <p className="mb-3 text-center text-[11px] font-medium leading-relaxed text-sky-900 sm:text-xs">
                      Prueba gratis por 14 días · Sin tarjeta · Acceso inmediato
                    </p>
                  )}
                  {isUs && (
                    <div className="mb-5 space-y-4">
                      <div className="space-y-1.5">
                        <h2 className="text-lg font-semibold tracking-tight text-slate-900">Crea tu cuenta médica</h2>
                        <p className="text-sm font-medium text-slate-700">Crea tu cuenta en menos de 1 minuto.</p>
                        <p className="text-sm leading-relaxed text-slate-600">
                          Empieza tu prueba gratis y conoce el flujo clínico de Aurora.
                        </p>
                      </div>
                      <p className="text-xs font-medium leading-relaxed text-sky-800">
                        Prueba gratis por 14 días · Sin tarjeta · Acceso inmediato
                      </p>
                      <div className="grid gap-2 sm:grid-cols-3">
                        <div className="rounded-xl border border-slate-200 bg-slate-50/90 px-2.5 py-2.5 text-center text-xs font-medium text-slate-700">
                          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">1</span>
                          Crea tu cuenta
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50/90 px-2.5 py-2.5 text-center text-xs font-medium text-slate-700">
                          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">2</span>
                          Evalúa un paciente
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50/90 px-2.5 py-2.5 text-center text-xs font-medium text-slate-700">
                          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">3</span>
                          Genera tu informe
                        </div>
                      </div>
                    </div>
                  )}
                  <form id="register-form" onSubmit={handleRegister} className={isUs ? "space-y-4" : "space-y-3"}>
                    <div className="space-y-1.5">
                      <Label htmlFor="register-nombre" className="text-xs font-medium text-slate-700">
                        {s.fullName}
                      </Label>
                      <Input
                        id="register-nombre"
                        type="text"
                        placeholder={s.placeholderName}
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        required
                        data-testid="register-name-input"
                        className={inputFieldClass}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="register-email" className="text-xs font-medium text-slate-700">
                        {s.email}
                      </Label>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder={s.placeholderEmail}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="register-email-input"
                        className={inputFieldClass}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="register-password" className="text-xs font-medium text-slate-700">
                        {s.password}
                      </Label>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        data-testid="register-password-input"
                        className={inputFieldClass}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="register-confirm-password" className="text-xs font-medium text-slate-700">
                        {s.confirmPassword}
                      </Label>
                      <Input
                        id="register-confirm-password"
                        type="password"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={6}
                        data-testid="register-confirm-password-input"
                        className={inputFieldClass}
                      />
                      {passwordsDoNotMatch && <p className="text-xs text-red-600">{s.pwdMismatch}</p>}
                    </div>
                  </form>
                  <RegisterPurchaseActions
                    loading={loading || passwordsDoNotMatch}
                    isUs={isUs}
                    acceptedTerms={acceptedTerms}
                    setAcceptedTerms={setAcceptedTerms}
                  />
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>

          {!isUs && (
            <>
              {!modoAula && (
              <div className="mt-3 rounded-xl border border-amber-200/90 bg-gradient-to-br from-amber-50 via-orange-50/65 to-amber-100/35 p-3 shadow-sm sm:mt-4">
                <h3 className="text-sm font-bold tracking-tight text-slate-900 sm:text-base">Suscripción desde $39 USD / mes</h3>
                <p className="mt-0.5 text-[11px] leading-snug text-slate-700 sm:text-xs">
                  Activa el acceso completo después de tu prueba.
                </p>
                <Button
                  type="button"
                  className="mt-3 h-9 w-full rounded-xl bg-orange-600 text-xs font-bold uppercase tracking-wide text-white subpixel-antialiased shadow-sm transition-colors hover:bg-orange-700 sm:h-10 sm:text-sm"
                  data-testid="login-subscribe-button"
                  onClick={handleSubscriptionCheckout}
                >
                  SUSCRIBIRME
                </Button>
                <p className="mt-1.5 text-center text-[9px] leading-snug text-slate-600 sm:text-[10px]">
                  Pago seguro con Lemon Squeezy (EE. UU.)
                </p>
              </div>
              )}

              <div className="mt-3 rounded-lg border border-slate-200/70 bg-white/90 px-2.5 py-2 shadow-sm sm:mt-4 sm:px-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-2">
                  <div className="flex gap-1.5 sm:max-w-[34%] sm:flex-1">
                    <Lock className="mt-0.5 h-3 w-3 shrink-0 text-sky-600" aria-hidden />
                    <p className="text-[10px] leading-snug text-slate-600">Tus datos están protegidos con encriptación avanzada</p>
                  </div>
                  <div className="flex gap-1.5 sm:max-w-[34%] sm:flex-1">
                    <ShieldCheck className="mt-0.5 h-3 w-3 shrink-0 text-sky-600" aria-hidden />
                    <p className="text-[10px] leading-snug text-slate-600">Plataforma confiable utilizada por profesionales de la salud</p>
                  </div>
                  <div className="flex gap-1.5 sm:max-w-[34%] sm:flex-1">
                    <Globe className="mt-0.5 h-3 w-3 shrink-0 text-sky-600" aria-hidden />
                    <p className="text-[10px] leading-snug text-slate-600">Accede desde cualquier lugar y dispositivo</p>
                  </div>
                </div>
              </div>

              <p className="mt-4 text-center text-[10px] text-slate-500 sm:text-[11px]">
                © 2026 Aurora Geriátrica. Todos los derechos reservados.
              </p>
            </>
          )}

          {isUs ? (
            <p className="mt-3 text-center text-[11px] text-slate-400 lg:hidden">
              {s.brandName} · © {new Date().getFullYear()}
            </p>
          ) : null}
        </div>
      </main>
    </div>
  );
}
